import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'
import { Purchases } from '@revenuecat/purchases-capacitor'
import { supabase } from '../lib/supabaseClient'
import { ensureActiveSession } from '../lib/activeSession'

const TRIAL_DAYS = 90 // 3 meses de prueba gratis
const REVENUECAT_ANDROID_API_KEY = 'goog_XmxUfSOvLoMxnZiuyrfRHixFPIO' // Project settings > API keys en RevenueCat

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [ready, setReady] = useState(false)
  const [access, setAccess] = useState({
    hasAccess: null,
    diasRestantes: 0,
    trialActive: false,
    trialEnds: null,
    isPremium: false,
    subscriptionExpiresAt: null,
  })

  const rcConfigurado = useRef(false)
  const userIdRef = useRef(null)

  useEffect(() => {
    let active = true
    let appListener = null

    async function checkAccess(userId) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('created_at, subscription_status, subscription_expires_at')
        .eq('id', userId)
        .single()

      const createdAt = profile?.created_at ? new Date(profile.created_at) : new Date()
      const trialEnds = new Date(createdAt.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000)
      const now = new Date()
      const trialActive = now < trialEnds
      const dias = Math.max(0, Math.ceil((trialEnds - now) / (24 * 60 * 60 * 1000)))
      const subActive = profile?.subscription_status === 'active' &&
        profile?.subscription_expires_at &&
        new Date(profile.subscription_expires_at) > now

      if (active) {
        setAccess({
          hasAccess: trialActive || subActive,
          diasRestantes: trialActive ? dias : 0,
          trialActive,
          trialEnds,
          isPremium: subActive,
          subscriptionExpiresAt: profile?.subscription_expires_at || null,
        })
      }
    }

    // Conecta el usuario de Supabase con RevenueCat (Purchases.logIn) para
    // que app_user_id en el webhook coincida con profiles.id. Solo aplica
    // en Android nativo — en web seguimos usando Wompi.
    async function sincronizarRevenueCat(userId) {
      if (!Capacitor.isNativePlatform()) return
      try {
        if (!rcConfigurado.current) {
          await Purchases.configure({ apiKey: REVENUECAT_ANDROID_API_KEY })
          rcConfigurado.current = true
        }
        if (userIdRef.current !== userId) {
          await Purchases.logIn({ appUserID: userId })
          userIdRef.current = userId
        }
      } catch (err) {
        console.error('Error configurando RevenueCat:', err)
      }
    }

    async function cerrarSesionRevenueCat() {
      if (!Capacitor.isNativePlatform() || !rcConfigurado.current) return
      try {
        await Purchases.logOut()
      } catch (err) {
        // logOut falla si ya es un usuario anónimo — no es crítico
      }
      userIdRef.current = null
    }

    // Cierra la sesión SOLO si la fila en active_sessions ya no existe
    // (porque la cerraste desde Perfil). Un token perdido o un last_seen
    // viejo no te sacan: en esos casos se crea/actualiza la fila.
    async function validarSesionActiva(userId) {
      const result = await ensureActiveSession(userId)
      if (result === 'revoked') {
        localStorage.removeItem('session_token_' + userId)
        await supabase.auth.signOut()
      }
    }

    async function init() {
      try {
        const { data: { session: s } } = await supabase.auth.getSession()
        if (!active) return
        setSession(s)
        if (s?.user) {
          await validarSesionActiva(s.user.id)
          await sincronizarRevenueCat(s.user.id)
          await checkAccess(s.user.id)
        }
      } catch (err) {
        console.error('Error en init de auth:', err)
      } finally {
        if (active) setReady(true)
      }
    }

    init()

    const { data: listener } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s)
      if (s?.user) {
        sincronizarRevenueCat(s.user.id).then(() => checkAccess(s.user.id))
      } else {
        cerrarSesionRevenueCat()
        setAccess({
          hasAccess: null,
          diasRestantes: 0,
          trialActive: false,
          trialEnds: null,
          isPremium: false,
          subscriptionExpiresAt: null,
        })
      }
    })

    // Pausa/reanuda el refresh de Supabase y revalida la sesión al volver a la app
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          supabase.auth.startAutoRefresh()
          try {
            const { data: { session: s } } = await supabase.auth.getSession()
            if (s?.user) await validarSesionActiva(s.user.id)
          } catch (err) {
            console.error('Error revalidando sesión:', err)
          }
        } else {
          supabase.auth.stopAutoRefresh()
        }
      }).then(h => {
        if (active) appListener = h
        else h.remove() // el efecto ya se limpió antes de que resolviera
      })
    }

    return () => {
      active = false
      listener.subscription.unsubscribe()
      appListener?.remove()
    }
  }, [])

  // Permite forzar un recálculo del acceso, por ejemplo justo después de
  // una compra en Android, sin esperar a que el webhook de RevenueCat
  // termine de sincronizar profiles.
  async function refrescarAcceso() {
    if (!session?.user) return
    const { data: profile } = await supabase
      .from('profiles')
      .select('created_at, subscription_status, subscription_expires_at')
      .eq('id', session.user.id)
      .single()

    const createdAt = profile?.created_at ? new Date(profile.created_at) : new Date()
    const trialEnds = new Date(createdAt.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000)
    const now = new Date()
    const trialActive = now < trialEnds
    const dias = Math.max(0, Math.ceil((trialEnds - now) / (24 * 60 * 60 * 1000)))
    const subActive = profile?.subscription_status === 'active' &&
      profile?.subscription_expires_at &&
      new Date(profile.subscription_expires_at) > now

    setAccess({
      hasAccess: trialActive || subActive,
      diasRestantes: trialActive ? dias : 0,
      trialActive,
      trialEnds,
      isPremium: subActive,
      subscriptionExpiresAt: profile?.subscription_expires_at || null,
    })
  }

  return (
    <AuthContext.Provider value={{ session, ready, ...access, refrescarAcceso }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}